import { useMemo } from 'react';

import { paths } from 'src/routes/paths';

import { useTranslate } from 'src/locales';

import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

const icon = (name: string) => <Iconify icon={name} />;

const ICONS = {
  dashboard: icon('solar:widget-bold-duotone'),
  employee: icon('solar:user-id-bold-duotone'),
  client: icon('solar:users-group-rounded-bold-duotone'),
  courier: icon('solar:delivery-bold-duotone'),
  dispatcher: icon('solar:user-speak-rounded-bold-duotone'),
  provider: icon('solar:shop-bold-duotone'),
  shipping: icon('solar:box-bold-duotone'),
  dispatch: icon('solar:clipboard-list-bold-duotone'),
  masterDispatch: icon('solar:folder-with-files-bold-duotone'),
  receptor: icon('solar:user-check-rounded-bold-duotone'),
  distributionClient: icon('solar:hand-shake-bold-duotone'),
  reception: icon('solar:scanner-bold-duotone'),
  manifest: icon('solar:checklist-minimalistic-bold-duotone'),
  claim: icon('solar:danger-triangle-bold-duotone'),
  locker: icon('solar:mailbox-bold-duotone'),
  passport: icon('solar:passport-bold-duotone'),
  migration: icon('solar:planet-bold-duotone'),
  remittance: icon('solar:card-send-bold-duotone'),
  reports: icon('solar:chart-bold-duotone'),
  reportsPassport: icon('solar:document-text-bold-duotone'),
  settings: icon('solar:settings-bold-duotone'),
  settingsShipping: icon('solar:box-minimalistic-bold-duotone'),
  settingsProcedure: icon('solar:tuning-2-bold-duotone'),
  cashClose: icon('solar:wallet-money-bold-duotone'),
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { t } = useTranslate();

  const data = useMemo(
    () => [
      // OVERVIEW
      // ----------------------------------------------------------------------
      {
        subheader: t('overview'),
        items: [
          {
            title: t('dashboard'),
            path: paths.dashboard.root,
            icon: ICONS.dashboard,
            roles: ['employee', 'owner'],
          },
          // Owner-only Daily Cash Close (#123). The route is also guarded by OwnerGuard.
          {
            title: t('Cash Close'),
            path: paths.owner.cashClose,
            icon: ICONS.cashClose,
            roles: ['owner'],
          },
          // {
          //   title: t('page one'),
          //   path: paths.dashboard.pageOne,
          //   icon: ICONS.analytics,
          // },
        ],
      },

      // DISTRIBUTION CLIENT
      // ----------------------------------------------------------------------
      {
        subheader: t('Distribution'),
        items: [
          {
            title: t('manifest'),
            roles: ['distribution-client'],
            path: paths.shipping.distributionClient.list,
            icon: ICONS.manifest,
          },
          {
            title: t('Profile'),
            roles: ['distribution-client'],
            path: paths.shipping.distributionClient.profile,
            icon: ICONS.employee,
          },
        ],
      },

      // MANAGEMENT
      // ----------------------------------------------------------------------
      {
        subheader: t('management'),
        items: [
          {
            title: t('employee'),
            roles: ['owner'],
            path: paths.management.employee.root,
            icon: ICONS.employee,
            children: [
              { title: t('list'), path: paths.management.employee.list, roles: ['owner'] },
              { title: t('create'), path: paths.management.employee.new, roles: ['owner'] },
            ],
          },
          {
            title: t('client'),
            roles: ['employee', 'owner'],
            path: paths.management.client.root,
            icon: ICONS.client,
            children: [
              {
                title: t('list'),
                path: paths.management.client.list,
                roles: ['employee', 'owner'],
              },
              {
                title: t('create'),
                path: paths.management.client.new,
                roles: ['employee', 'owner'],
              },
              {
                title: t('driver_license_scanning'),
                path: paths.management.client.driverLicenseProcessing,
                roles: ['employee', 'owner'],
              },
            ],
          },
        ],
      },
      {
        subheader: t('shipping_entities'),
        items: [
          {
            title: t('courier'),
            roles: ['owner'],
            path: paths.shipping.courier.root,
            icon: ICONS.courier,
            children: [
              { title: t('list'), path: paths.shipping.courier.list, roles: ['owner'] },
              { title: t('create'), path: paths.shipping.courier.new, roles: ['owner'] },
            ],
          },
          {
            title: t('dispatcher'),
            roles: ['owner'],
            path: paths.shipping.dispatcher.root,
            icon: ICONS.dispatcher,
            children: [
              { title: t('list'), path: paths.shipping.dispatcher.list, roles: ['owner'] },
              { title: t('create'), path: paths.shipping.dispatcher.new, roles: ['owner'] },
            ],
          },
          {
            title: t('provider'),
            roles: ['owner'],
            path: paths.shipping.provider.root,
            icon: ICONS.provider,
            children: [
              { title: t('list'), path: paths.shipping.provider.list, roles: ['owner'] },
              { title: t('create'), path: paths.shipping.provider.new, roles: ['owner'] },
            ],
          },
          {
            title: t('receptor'),
            roles: ['owner'],
            path: paths.shipping.receptor.root,
            icon: ICONS.receptor,
            children: [
              { title: t('list'), path: paths.shipping.receptor.list, roles: ['owner'] },
              { title: t('create'), path: paths.shipping.receptor.new, roles: ['owner'] },
            ],
          },
          {
            title: t('distribution client'),
            roles: ['employee', 'owner'],
            path: paths.shipping.distributionClientManagement.root,
            icon: ICONS.distributionClient,
            children: [
              {
                title: t('list'),
                path: paths.shipping.distributionClientManagement.list,
                roles: ['employee', 'owner'],
              },
              {
                title: t('create'),
                path: paths.shipping.distributionClientManagement.new,
                roles: ['employee', 'owner'],
              },
            ],
          },
        ],
      },
      {
        subheader: t('shipping'),
        items: [
          {
            title: t('shipping'),
            roles: ['employee', 'owner'],
            path: paths.shipping.shipping.root,
            icon: ICONS.shipping,
            children: [
              {
                title: t('list'),
                path: paths.shipping.shipping.list,
                roles: ['employee', 'owner'],
              },
              {
                title: t('create'),
                path: paths.shipping.shipping.new,
                roles: ['employee', 'owner'],
              },
            ],
          },
          {
            title: t('dispatch'),
            roles: ['employee', 'owner'],
            path: paths.shipping.dispatch.root,
            icon: ICONS.dispatch,
            children: [
              {
                title: t('list'),
                path: paths.shipping.dispatch.list,
                roles: ['employee', 'owner'],
              },
              {
                title: t('create'),
                path: paths.shipping.dispatch.new,
                roles: ['employee', 'owner'],
              },
            ],
          },
          {
            title: t('Master Dispatch'),
            roles: ['employee', 'owner'],
            path: paths.shipping.masterDispatch.root,
            icon: ICONS.masterDispatch,
            children: [
              {
                title: t('list'),
                path: paths.shipping.masterDispatch.list,
                roles: ['employee', 'owner'],
              },
            ],
          },
          {
            title: t('manifest'),
            roles: ['employee', 'owner'],
            path: paths.shipping.manifest.root,
            icon: ICONS.manifest,
            children: [
              {
                title: t('list'),
                path: paths.shipping.manifest.list,
                roles: ['employee', 'owner'],
              },
            ],
          },
          {
            title: t('reception'),
            roles: ['employee', 'owner'],
            path: paths.shipping.reception.root,
            icon: ICONS.reception,
            children: [
              {
                title: t('list'),
                path: paths.shipping.reception.list,
                roles: ['employee', 'owner'],
              },
              {
                title: t('create'),
                path: paths.shipping.reception.new,
                roles: ['employee', 'owner'],
              },
            ],
          },
          {
            title: t('distribution'),
            roles: ['employee', 'owner'],
            path: paths.shipping.distribution.root,
            icon: ICONS.manifest,
            children: [
              {
                title: t('list'),
                path: paths.shipping.distribution.list,
                roles: ['employee', 'owner'],
              },
            ],
          },
          {
            title: t('distributor manifest'),
            roles: ['employee', 'owner'],
            path: paths.shipping.distributorManifest.root,
            icon: ICONS.manifest,
            children: [
              {
                title: t('list'),
                path: paths.shipping.distributorManifest.list,
                roles: ['employee', 'owner'],
              },
            ],
          },
          {
            title: t('claim'),
            roles: ['employee', 'owner'],
            path: paths.shipping.claim.root,
            icon: ICONS.claim,
            children: [
              {
                title: t('list'),
                path: paths.shipping.claim.list,
                roles: ['employee', 'owner'],
              },
            ],
          },
          {
            title: t('Locker'),
            roles: ['employee', 'owner'],
            path: paths.locker.root,
            icon: ICONS.locker,
            children: [
              {
                title: t('list'),
                path: paths.locker.packages,
                roles: ['employee', 'owner'],
              },
            ],
          },
        ],
      },
      {
        subheader: t('procedure'),
        items: [
          {
            title: t('passport'),
            roles: ['employee', 'owner'],
            path: paths.procedure.passport.root,
            icon: ICONS.passport,
            children: [
              {
                title: t('list'),
                path: paths.procedure.passport.list,
                roles: ['employee', 'owner'],
              },
              {
                title: t('create'),
                path: paths.procedure.passport.new,
                roles: ['employee', 'owner'],
              },
              {
                title: t('passport_scanning'),
                path: paths.procedure.passport.photoProcessing,
                roles: ['employee', 'owner'],
              },
            ],
          },
          {
            title: t('migration'),
            roles: ['employee', 'owner'],
            path: paths.procedure.migration.root,
            icon: ICONS.migration,
            children: [
              {
                title: t('list'),
                path: paths.procedure.migration.list,
                roles: ['employee', 'owner'],
              },
              {
                title: t('create'),
                path: paths.procedure.migration.new,
                roles: ['employee', 'owner'],
              },
            ],
          },
          {
            title: t('remittance'),
            roles: ['employee', 'owner'],
            path: paths.procedure.remittance.root,
            icon: ICONS.remittance,
            children: [
              {
                title: t('list'),
                path: paths.procedure.remittance.list,
                roles: ['employee', 'owner'],
              },
              {
                title: t('create'),
                path: paths.procedure.remittance.new,
                roles: ['employee', 'owner'],
              },
            ],
          },
        ],
      },

      // REPORTS
      // ----------------------------------------------------------------------
      {
        subheader: t('Reports'),
        items: [
          {
            title: t('General'),
            roles: ['owner'],
            path: paths.reports.root,
            icon: ICONS.reports,
            children: [
              { title: t('Reports General'), path: paths.reports.root, roles: ['owner'] },
              {
                title: t('Client Transactions'),
                path: paths.reports.clientTransactions,
                roles: ['owner'],
              },
            ],
          },
          {
            title: t('Passports'),
            roles: ['employee', 'owner'],
            path: paths.reports.expiringPassports,
            icon: ICONS.reportsPassport,
            children: [
              {
                title: t('Expiring Passports'),
                path: paths.reports.expiringPassports,
                roles: ['employee', 'owner'],
              },
            ],
          },
        ],
      },
      // Settings
      // ----------------------------------------------------------------------
      {
        subheader: t('Settings'),
        items: [
          {
            title: t('General'),
            roles: ['owner'],
            path: paths.settings.root,
            icon: ICONS.settings,
            children: [{ title: t('Agency'), path: paths.settings.root, roles: ['owner'] }],
          },
          {
            title: t('Shipping'),
            roles: ['owner'],
            path: paths.settings.root,
            icon: ICONS.settingsShipping,
            children: [
              {
                title: t('Shipping Services Type'),
                path: paths.settings.servicesType,
                roles: ['owner'],
              },
              { title: t('Packages Type'), path: paths.settings.packagesType, roles: ['owner'] },
              { title: t('Delivery Type'), path: paths.settings.deliveryType, roles: ['owner'] },

              // { title: t('Content Type'), path: paths.settings.contentType, roles: ['owner'] },
              {
                title: t('Content Type'),
                path: paths.settings.contentType,
                children: [
                  {
                    title: t('Content Type per Unit'),
                    path: paths.settings.contentType,
                  },
                  {
                    title: t('Content Type per Range'),
                    path: paths.settings.contentTypeRange,
                  },
                ],
                roles: ['owner'],
              },
              {
                title: t('Content Categories'),
                path: paths.settings.contentCategory,
                roles: ['owner'],
              },
              {
                title: t('Products'),
                path: paths.settings.products.list,
                roles: ['owner'],
              },
              {
                title: t('Pickup Location'),
                path: paths.settings.pickupLocation,
                roles: ['owner'],
              },
              // {
              //   title: t('Provincia'),
              //   path: paths.settings.provincia,
              //   roles: ['owner'],
              // },
              // {
              //   title: t('Municipio'),
              //   path: paths.settings.municipio,
              //   roles: ['owner'],
              // },
              {
                title: t('Payment Method'),
                path: paths.settings.payment,
                roles: ['owner'],
              },
            ],
          },
          {
            title: t('Procedure'),
            roles: ['owner'],
            path: paths.settings.root,
            icon: ICONS.settingsProcedure,
            children: [
              {
                title: t('Passport Categories'),
                path: paths.settings.passportCategoryType,
                roles: ['owner'],
              },
              {
                title: t('Remittance Delivery Type'),
                path: paths.settings.remittanceDeliveryType,
                roles: ['owner'],
              },
              {
                title: t('Remittance Pickup Locations'),
                path: paths.settings.remittancePickupLocation,
                roles: ['owner'],
              },
              {
                title: t('Migration Forms'),
                path: paths.settings.migrationForms,
                roles: ['owner'],
              },
            ],
          },
        ],
      },
    ],
    [t]
  );

  return data;
}
